import {
  INQUIRY_DEFAULT_TEMPLATE,
  INQUIRY_TEMPLATE_MAP,
  REPORT_DEFAULT_TEMPLATE,
  ILLEGAL_DEFAULT_TEMPLATE,
  OBJECTION_DEFAULT_TEMPLATE,
  FORM_TYPE_INQUIRY,
  FORM_TYPE_REPORT,
  FORM_TYPE_ILLEGAL,
  FORM_TYPE_OBJECTION,
} from '@/constants/help/index';
import {
  FormType,
  FormConfig,
  TemplateBuilderOptions,
} from '@/components/help/common/FormContainer/types';
/* ---------- Form 타입 관련 유틸 함수 ---------- */

/* ---------- 이메일 관련 ---------- */
const EMAIL_ID_REG = /^[A-Za-z0-9._-]+$/; // 허용 문자
const FULL_EMAIL_REG = /^[A-Za-z0-9._%+-]{3,}@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

export const splitEmail = (value = '') => {
  const [emailId = '', domain = ''] = value.split('@');
  return {emailId, domain};
};

// 로컬 파트 문자가 모두 유효한가?
export const isEmailIdValid = (emailId: string) => EMAIL_ID_REG.test(emailId);

// `xxx@` 형태의 부분 입력이 유효한가?
export const isPartialEmail = (value: string) => {
  const {emailId, domain} = splitEmail(value);
  return (
    value?.includes('@') &&
    domain === '' &&
    isEmailIdValid(emailId) &&
    emailId.length >= 3
  );
};

// 전체 이메일 형식이 유효한가?
export const isFullEmail = (value: string) => FULL_EMAIL_REG.test(value);

// 드롭다운을 열어야 하는가? (`xxx@` 단계 전용)
export const shouldOpenDropdown = (value: string) => isPartialEmail(value);

/* ---------- content 관련 ---------- */

const formConfigs: Record<FormType, FormConfig> = {
  [FORM_TYPE_INQUIRY]: {
    categoryMap: INQUIRY_TEMPLATE_MAP,
    defaultTemplate: INQUIRY_DEFAULT_TEMPLATE,
  },
  [FORM_TYPE_REPORT]: {
    defaultTemplate: REPORT_DEFAULT_TEMPLATE,
  },
  [FORM_TYPE_ILLEGAL]: {
    defaultTemplate: ILLEGAL_DEFAULT_TEMPLATE,
  },
  [FORM_TYPE_OBJECTION]: {
    defaultTemplate: OBJECTION_DEFAULT_TEMPLATE,
  },
};

/**
 * 폼 타입과 카테고리에 맞는 내용(content) 템플릿을 반환
 * @param formType 'inquiry', 'report' 등 폼의 종류
 * @param category 선택된 카테고리 값 (e.g., 'NODE0000000549')
 * @param isGuest 게스트 여부
 * @returns {string} 생성된 내용 템플릿
 */
export const getFormContentTemplate = (
  formType: FormType,
  category: string,
  isGuest: boolean = false,
  isMobile: boolean = false,
): string => {
  const config = formConfigs[formType];
  const templateBuilder = config?.categoryMap?.get(category);

  if (!templateBuilder) {
    return '';
  }

  const options: TemplateBuilderOptions = {
    isGuest: isGuest,
    isMobile: isMobile,
  };
  return templateBuilder(options);
};

/**
 * 폼 타입에 맞는 기본 내용(content) 템플릿을 반환합니다.
 * @param formType 'inquiry', 'report' 등 폼의 종류
 * @returns {string} 기본 안내 문구
 */
export const getFormDefaultTemplate = (formType: FormType): string => {
  return formConfigs[formType]?.defaultTemplate || '';
};

/**
 * 콘텐츠가 비어있거나 템플릿과 동일한지 확인
 */
export const isContentEmptyOrTemplate = (
  content: string,
  template: string,
): boolean => {
  const contentTrimmed = content.trim();
  const templateTrimmed = template.trim();

  return contentTrimmed === '' || contentTrimmed === templateTrimmed;
};

/**
 * 콘텐츠 길이 유효성 검사: 전체 콘텐츠 길이가 최소 길이 이상인지 검사
 */
export const validateContentLength = (
  content: string,
  minLength: number = 5,
): boolean => {
  const contentTrimmed = content.trim();

  if (contentTrimmed.length < minLength) {
    return false;
  }

  return true;
};

/**
 * 숫자만 입력 가능하도록 하는 핸들러
 */
export const handleNumericInput = (e) => {
  // 숫자가 아닌 문자 제거
  e.target.value = e.target.value.replace(/[^0-9]/g, '');
};
